import { FormArray, FormControl, FormGroup } from '@angular/forms';

import {
  ExtendedRankSortQuestionType,
  ExtendedRatingQuestionType,
  QuestionCard,
  QuestionOption,
  QuestionPrefix,
  QuestionType,
  SurveyModel
} from '@empathy/survey/base';
import { PageModel, Question } from '@empathy/survey-core';

import { NotepadForm, NotepadQuestionForm } from './notepad-form.model';
import { NotepadModel, NotepadQuestion } from './notepad-model.model';
import { ExtendedQuestionType } from './notepad-question-type.constant';

export const NotepadIdPrefix = 'NQ';

export class Notepad {
  research: {
    title: string;
    description: string;
  };

  form = new FormGroup<NotepadForm>({
    programmerNotes: new FormControl<string>(''),
    questions: new FormArray<FormGroup<NotepadQuestionForm>>([])
  });

  get model(): NotepadModel {
    return this.mapToNotepadModel();
  }

  get surveyJson(): string {
    return JSON.stringify(this.mapToSurveyModel());
  }

  get JSON(): string {
    return JSON.stringify(this.model);
  }

  set JSON(notepad: string) {
    const model = JSON.parse(notepad);
    this.patch(model);
  }

  private patch(notepad: NotepadModel): void {
    this.form.patchValue(notepad);

    if (!notepad.questions) {
      return;
    }

    notepad.questions.forEach((question) => {
      const questionForm = new FormGroup(new NotepadQuestionForm(question));
      (this.form.get('questions') as FormArray).push(questionForm);
    });
  }

  private mapToNotepadModel(): NotepadModel {
    const formValues = this.form.value;
    return {
      programmerNotes: formValues.programmerNotes,
      questions: formValues.questions
    } as NotepadModel;
  }

  private mapToSurveyModel(): SurveyModel {
    const survey = new SurveyModel();
    survey.showTitleInd = true;
    survey.programmerNotes = this.form.value.programmerNotes;
    survey.pages = this.mapPages(this.model);
    survey.questionsHidden = [];

    return survey;
  }

  private mapPages(notepad: NotepadModel): PageModel[] {
    const pages: PageModel[] = [];

    notepad.questions.forEach((question, index) => {
      const page = new PageModel();

      const questionModel = new Question();

      const cards: QuestionCard[] = this.mapQuestionCards(question);
      const options: QuestionOption[] = this.mapQuestionOptions(question);
      const ranks: QuestionOption[] = this.mapQuestionRanks(question);

      questionModel.id = `${QuestionPrefix.Question}${index + 1}`;
      questionModel.cards = cards;
      questionModel.options = options;
      questionModel.ranks = ranks;
      questionModel.title = question.questionText;
      questionModel.description = question.instructions;
      questionModel.programmerNotes = question.programmerNotes;
      questionModel.type = this.mapQuestionType(question.questionType);
      questionModel.requiredInd = question.mandatoryInd;
      questionModel.randomizedOptionsInd = question.randomizeOptionsInd;
      questionModel.ratingType = question.ratingType;
      questionModel.useOtherInd = question.otherOptionInd;
      questionModel.useNoneInd = question.noneOptionInd;

      const otherOption = question.options.find((option) => option === 'Other');
      if (otherOption) {
        questionModel.otherLabel = otherOption;
      }

      const noneOption = question.options.find((option) => option === 'None');
      if (noneOption) {
        questionModel.noneLabel = noneOption;
      }

      page.id = `page_${index + 1}`;
      page.questions = [questionModel];

      pages.push(page);
    });

    return pages;
  }

  private mapQuestionCards(question: NotepadQuestion): QuestionCard[] {
    const cards: QuestionCard[] = [];
    question.cards.forEach((card, cardIndex) => {
      const cardModel = new QuestionCard();

      cardModel.id = `card_${cardIndex + 1}`;
      cardModel.text = card;

      cards.push(cardModel);
    });

    return cards;
  }

  private mapQuestionOptions(question: NotepadQuestion): QuestionOption[] {
    const options: QuestionOption[] = [];
    question.options.forEach((option, questionIndex) => {
      const optionModel = new QuestionOption();

      optionModel.id = `opt_${questionIndex + 1}`;
      optionModel.text = option;
      optionModel.value = `${questionIndex + 1}`;

      options.push(optionModel);
    });

    return options;
  }

  private mapQuestionRanks(question: NotepadQuestion): QuestionOption[] {
    const ranks: QuestionOption[] = [];
    question.ranks.forEach((rank, questionIndex) => {
      const optionModel = new QuestionOption();

      optionModel.id = `rank_${questionIndex + 1}`;
      optionModel.text = rank;
      optionModel.value = `${questionIndex + 1}`;

      ranks.push(optionModel);
    });

    return ranks;
  }

  private mapQuestionType(type: ExtendedQuestionType): QuestionType {
    if (
      type === ExtendedRatingQuestionType.RatingNumeric ||
      type === ExtendedRatingQuestionType.RatingDescriptive ||
      type === ExtendedRatingQuestionType.RatingStars
    ) {
      return QuestionType.Rating;
    }

    if (type === ExtendedRankSortQuestionType.RankSortNumeric || type === ExtendedRankSortQuestionType.RankSortDescriptive) {
      return QuestionType.RankSort;
    }

    return type;
  }
}
